import React, { useRef, useState, useEffect, useMemo } from 'react';
import { Stage, Layer, Group, Circle, Rect, RegularPolygon, Text } from 'react-konva';
import { isChild } from './seatingMoves';
import { buildSeatMap } from './seatingMoves';

/**
 * Interactive 2D floor plan (Phase 2 + move/multi-select wave).
 *  • drag a table to reposition it (persists x/y)
 *  • drag an UNSEATED guest onto a table to seat them (or click a guest, then a table)
 *  • drag an ALREADY-SEATED guest onto another table to MOVE them
 *  • multi-select (✓) a family/group and move them all at once (drag any selected chip, or click a table)
 *  • change a seated guest's table inline from the "Seated" roster
 *  • any move that over-fills a table, seats a conflict together, breaks accessibility, or splits a
 *    family routes through the parent's MoveResolver instead of silently failing
 *  • print or email (via Communications) the seated roster from the rail
 * All writes go through the parent's governed handlers (onRequestMove → PATCH /seating/assignments).
 */
const STAGE_H = 540;
const tableRadius = (t) => (Number(t.w) > 0 ? Math.max(Number(t.w), Number(t.h) || Number(t.w)) / 2 : Math.max(32, 13 + (Number(t.seat_count) || 8) * 2.1));

export default function FloorPlanCanvas({
  tables, assignments, poolList, groupColor, relGroups, fixtures,
  onMoveTable, onMoveFixture, onRemoveFixture, onEditFixture,
  onAssign, onSeatGroup, onUnseat, onProtect, onEditTable,
  selectedIds = [], onToggleSelect, onClearSelection, onRequestMove, onPrintRoster, onEmailRoster,
  onAssignSeat, onSwapSeats,
  // Presentation aids (host 2026-07-21): locked = view-only (pan/zoom/hover still
  // work, nothing can be moved or edited); stageHeight overrides the 540px canvas
  // height for the full-view overlay.
  locked = false, stageHeight,
}) {
  const wrapRef = useRef(null);
  const stageRef = useRef(null);
  const dragIdsRef = useRef([]);                   // guest ids currently being dragged (selection-aware)
  const [width, setWidth] = useState(760);
  const [scale, setScale] = useState(1);
  const [stagePos, setStagePos] = useState({ x: 0, y: 0 });
  const [sel, setSel] = useState(null);            // single unseated guest queued by click-to-seat
  const [selGroup, setSelGroup] = useState(null);  // { key, name, ids } — a group queued to seat together
  const [byGroup, setByGroup] = useState(false);   // rail view: cluster unseated guests by group
  const [hoverT, setHoverT] = useState(null);      // { id, cx, cy } — table hovered → who's seated popover

  useEffect(() => {
    const el = wrapRef.current; if (!el) return;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el); setWidth(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  const guestsById = useMemo(() => { const m = {}; (poolList || []).forEach((g) => { m[g.id] = g; }); return m; }, [poolList]);
  const tablesById = useMemo(() => { const m = {}; (tables || []).forEach((t) => { m[t.id] = t; }); return m; }, [tables]);
  const assignByGuest = useMemo(() => { const m = {}; (assignments || []).forEach((a) => { m[a.guest_id] = a.table_id; }); return m; }, [assignments]);
  const protById = useMemo(() => { const m = {}; (assignments || []).forEach((a) => { m[a.guest_id] = !!a.protected; }); return m; }, [assignments]);
  const gc = groupColor || {};
  const selSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const assignsByTable = useMemo(() => {
    const m = {}; (assignments || []).forEach((a) => { if (a.table_id) (m[a.table_id] = m[a.table_id] || []).push(a.guest_id); }); return m;
  }, [assignments]);
  // seatMap: per table, an array of length seat_count placing each guest at their SEAT INDEX
  // (1-based). This makes the floor honor exact seats — drop a guest on chair 3, they render on
  // chair 3 and stay there. Guests with no seat_index fall into the first open chair.
  const seatMapByTable = useMemo(() => buildSeatMap(tables, assignments), [tables, assignments]);
  const currentSeatOf = (gid) => {
    for (const t of (tables || [])) { const arr = seatMapByTable[t.id] || []; const i = arr.indexOf(gid); if (i >= 0) return { tableId: t.id, seatIndex: i + 1 }; }
    return null;
  };

  // a table is "in conflict" if it seats two guests where one lists the other in conflicts_with
  const conflictTables = useMemo(() => {
    const set = new Set();
    Object.entries(assignsByTable).forEach(([tid, ids]) => {
      for (const gid of ids) {
        const cw = (guestsById[gid] && guestsById[gid].conflicts_with) || [];
        if (cw.some((x) => ids.includes(x))) { set.add(tid); break; }
      }
    });
    return set;
  }, [assignsByTable, guestsById]);

  const cols = Math.max(1, Math.floor((width - 60) / 175));
  const posOf = (t, i) => ((t.x || t.y) ? { x: t.x, y: t.y } : { x: 95 + (i % cols) * 175, y: 95 + Math.floor(i / cols) * 160 });

  const unseated = (poolList || []).filter((g) => !assignByGuest[g.id]);
  const seated = (poolList || []).filter((g) => assignByGuest[g.id]);

  // Group-based seating — cluster the UNSEATED guests into parties/households/relationship-groups.
  const relGroupByGuest = useMemo(() => {
    const m = {};
    (relGroups || []).forEach((grp) => (grp.guest_ids || []).forEach((gid) => { if (!m[gid]) m[gid] = { key: `rg:${grp.id || grp.name}`, name: grp.name || 'Group' }; }));
    return m;
  }, [relGroups]);
  const groups = useMemo(() => {
    const map = new Map();
    unseated.forEach((g) => {
      const rg = relGroupByGuest[g.id];
      let key; let name;
      if (rg) { key = rg.key; name = rg.name; }
      else if (g.party_id) { key = `pty:${g.party_id}`; name = g.household || 'Household'; }
      else if (g.household) { key = `hh:${g.household.toLowerCase()}`; name = g.household; }
      else { key = `solo:${g.id}`; name = g.name; }
      if (!map.has(key)) map.set(key, { key, name, members: [] });
      map.get(key).members.push(g);
    });
    const real = []; const singles = [];
    map.forEach((grp) => { if (grp.members.length >= 2) real.push(grp); else singles.push(grp.members[0]); });
    real.sort((a, b) => b.members.length - a.members.length || (a.name || '').localeCompare(b.name || ''));
    return { real, singles };
  }, [unseated, relGroupByGuest]);

  const openSeats = (t) => Math.max(0, (Number(t.seat_count) || 8) - (assignsByTable[t.id] || []).length);

  // Seat a queued group at a table — routed through the resolver so capacity/conflicts are handled.
  const placeGroupAt = (ids, t) => { if (ids && ids.length && onRequestMove) onRequestMove(ids, t.id); setSelGroup(null); setSel(null); };
  const seatGroupAuto = (grp) => {
    const ids = grp.members.map((g) => g.id);
    const fit = (tables || []).find((t) => openSeats(t) >= ids.length);
    if (fit) { if (onRequestMove) onRequestMove(ids, fit.id); setSelGroup(null); setSel(null); }
    else { setSelGroup({ key: grp.key, name: grp.name, ids }); setSel(null); }
  };

  // Selection-aware drag: dragging a selected chip carries the whole selection.
  const beginDrag = (e, gid) => {
    const ids = (selSet.has(gid) && selectedIds.length > 1) ? selectedIds.slice() : [gid];
    dragIdsRef.current = ids;
    try { e.dataTransfer.setData('text/plain', JSON.stringify(ids)); e.dataTransfer.effectAllowed = 'move'; } catch (err) { /* */ }
  };
  const endDrag = () => { dragIdsRef.current = []; };

  const onWheel = (e) => {
    e.evt.preventDefault();
    const s = stageRef.current; const old = scale; const ptr = s.getPointerPosition();
    const ns = Math.max(0.45, Math.min(2.2, old * (e.evt.deltaY > 0 ? 0.92 : 1.08)));
    const mp = { x: (ptr.x - stagePos.x) / old, y: (ptr.y - stagePos.y) / old };
    setScale(ns); setStagePos({ x: ptr.x - mp.x * ns, y: ptr.y - mp.y * ns });
  };

  // Hit-test a screen point to a table id (accounting for pan/zoom).
  const tableIdAtClient = (cx, cy) => {
    const rect = wrapRef.current.getBoundingClientRect();
    const x = (cx - rect.left - stagePos.x) / scale, y = (cy - rect.top - stagePos.y) / scale;
    for (let i = 0; i < tables.length; i++) {
      const t = tables[i], p = posOf(t, i), r = tableRadius(t) + 14;
      if (Math.hypot(x - p.x, y - p.y) <= r) return t.id;
    }
    return null;
  };
  // Hit-test a screen point to the NEAREST CHAIR — returns {tableId, seatIndex, occupant}.
  // This is what makes the floor a real seat editor: drop on a specific chair → that exact seat.
  const seatAtClient = (cx, cy) => {
    const rect = wrapRef.current.getBoundingClientRect();
    const x = (cx - rect.left - stagePos.x) / scale, y = (cy - rect.top - stagePos.y) / scale;
    let best = null; let bestD = 17;          // pick the closest chair within ~17 world px
    for (let i = 0; i < tables.length; i++) {
      const t = tables[i], p = posOf(t, i), cap = Number(t.seat_count) || 8, sr = tableRadius(t) + 16;
      for (let si = 0; si < cap; si++) {
        const a = (si / cap) * Math.PI * 2;
        const sx = p.x + Math.cos(a) * sr, sy = p.y + Math.sin(a) * sr;
        const d = Math.hypot(x - sx, y - sy);
        if (d <= bestD) { bestD = d; best = { tableId: t.id, seatIndex: si + 1, occupant: (seatMapByTable[t.id] || [])[si] || null }; }
      }
    }
    return best;
  };
  // Place one guest on an exact chair. Empty → sit. Occupied by someone else → swap (if the dragged
  // guest was already seated) or bump the occupant to a free chair at that table (if dragged from the
  // unseated rail). They land EXACTLY where you drop them.
  const placeAtSeat = (gid, seat) => {
    const occupant = seat.occupant;
    if (occupant && occupant === gid) return;            // dropped on their own chair
    const from = currentSeatOf(gid);
    if (!occupant) { if (onAssignSeat) onAssignSeat(gid, seat.tableId, seat.seatIndex); return; }
    if (from) { if (onSwapSeats) onSwapSeats(gid, from.tableId, from.seatIndex, occupant, seat.tableId, seat.seatIndex); return; }
    // unseated guest dropped on an occupied chair → seat them at the first free chair of that table
    const arr = seatMapByTable[seat.tableId] || []; const free = arr.findIndex((x) => x === null);
    if (free >= 0) { if (onAssignSeat) onAssignSeat(gid, seat.tableId, free + 1); }
    else if (onRequestMove) onRequestMove([gid], seat.tableId);   // table full → governed path
  };
  const onCanvasDrop = (e) => {
    e.preventDefault();
    let ids = [];
    try { ids = JSON.parse(e.dataTransfer.getData('text/plain') || '[]'); } catch (err) { ids = []; }
    if (!Array.isArray(ids) || !ids.length) ids = dragIdsRef.current || [];
    if (!ids.length) { endDrag(); return; }
    const seat = seatAtClient(e.clientX, e.clientY);
    if (seat && ids.length === 1) { placeAtSeat(ids[0], seat); endDrag(); return; }
    // multi-select group, or dropped between chairs → table-level (sits them together)
    const tid = (seat && seat.tableId) || tableIdAtClient(e.clientX, e.clientY);
    if (tid && onRequestMove) onRequestMove(ids, tid);
    endDrag();
  };

  const tableStroke = (t, occN, conflict) => (conflict ? '#b04a3a' : (occN ? '#C6A75A' : '#cfcfd3'));

  // small ✓ select control shared by chips
  const SelDot = ({ id }) => (
    <button className={`as-gcheck ${selSet.has(id) ? 'on' : ''}`} title={selSet.has(id) ? 'Selected' : 'Select to move with others'}
      onClick={(e) => { e.stopPropagation(); onToggleSelect && onToggleSelect(id); }}>{selSet.has(id) ? '✓' : ''}</button>
  );

  return (
    <div className="as-canvas-wrap">
      <div className="as-canvas" ref={wrapRef}
        role="application"
        style={stageHeight ? { height: stageHeight } : undefined}
        aria-label={`Seating floor plan: ${tables.length} tables.${locked ? ' Locked — view only.' : ' Drag tables to arrange, drag guests onto a table to seat or move them.'} A text list of every guest and their table is in the panel beside this canvas.`}
        onDragOver={(e) => e.preventDefault()}
        onDrop={locked ? undefined : onCanvasDrop}>
        <Stage ref={stageRef} width={width} height={stageHeight || STAGE_H} scaleX={scale} scaleY={scale} x={stagePos.x} y={stagePos.y}
          draggable onWheel={onWheel}
          onDragEnd={(e) => { if (e.target === e.target.getStage()) setStagePos({ x: e.target.x(), y: e.target.y() }); }}
          onClick={(e) => { if (e.target === e.target.getStage()) setSel(null); }}
          onTap={(e) => { if (e.target === e.target.getStage()) setSel(null); }}>
          <Layer listening>
            {(fixtures || []).map((f) => {
              const w = Number(f.w) || 60; const h = Number(f.h) || 60; const isC = f.shape === 'circle';
              const lw = isC ? Math.max(w, h) : w;
              const labelY = isC ? -7 : (-h / 2 + 9);
              return (
                <Group key={f.id} x={f.x || 450} y={f.y || 300} rotation={f.rotation || 0} draggable={!locked}
                  onDragEnd={(e) => onMoveFixture && onMoveFixture(f.id, Math.round(e.target.x()), Math.round(e.target.y()))}
                  onClick={(e) => { e.cancelBubble = true; if (!locked && onEditFixture) onEditFixture(f); }}
                  onTap={(e) => { e.cancelBubble = true; if (!locked && onEditFixture) onEditFixture(f); }}
                  onMouseEnter={(e) => { const c = e.target.getStage().container(); if (c) c.style.cursor = 'pointer'; }}
                  onMouseLeave={(e) => { const c = e.target.getStage().container(); if (c) c.style.cursor = 'default'; }}>
                  {isC
                    ? <Circle radius={Math.max(w, h) / 2} fill="rgba(198,167,90,.07)" stroke="#C6A75A" dash={[7, 5]} strokeWidth={1.4} />
                    : <Rect x={-w / 2} y={-h / 2} width={w} height={h} cornerRadius={10} fill="rgba(198,167,90,.07)" stroke="#C6A75A" dash={[7, 5]} strokeWidth={1.4} />}
                  <Text text={f.label || f.type} x={-lw / 2} y={labelY} width={lw} align="center" fontFamily="Playfair Display, Georgia, serif" fontSize={12.5} fill="#6F685C" listening={false} />
                </Group>
              );
            })}
          </Layer>
          <Layer>
            {tables.map((t, i) => {
              const p = posOf(t, i); const occ = assignsByTable[t.id] || []; const conflict = conflictTables.has(t.id);
              const smap = seatMapByTable[t.id] || [];     // guests placed by exact seat index
              const cap = Number(t.seat_count) || 8;
              const isRect = t.shape === 'rectangle' || t.shape === 'head' || t.shape === 'sweetheart';
              const isSquare = t.shape === 'square';
              const sized = Number(t.w) > 0;
              const baseR = Math.max(32, 13 + cap * 2.1);
              const W = sized ? Number(t.w) : baseR * 2;
              const H = sized ? (Number(t.h) || Number(t.w)) : (isRect ? baseR : W);
              const rad = Math.max(W, H) / 2;
              const armed = selGroup || sel || selectedIds.length > 0;
              const seatThis = () => {
                if (selGroup) { placeGroupAt(selGroup.ids, t); return; }
                if (selectedIds.length > 0) { if (onRequestMove) onRequestMove(selectedIds, t.id); return; }
                if (sel) { if (onRequestMove) onRequestMove([sel], t.id); setSel(null); return; }
                onEditTable(t);
              };
              const stroke = tableStroke(t, occ.length, conflict);
              return (
                <Group key={t.id} x={p.x} y={p.y} draggable={!locked}
                  onDragEnd={(e) => { setHoverT(null); onMoveTable(t.id, Math.round(e.target.x()), Math.round(e.target.y())); }}
                  onClick={(e) => { e.cancelBubble = true; if (!locked) seatThis(); }}
                  onTap={(e) => { e.cancelBubble = true; if (!locked) seatThis(); }}
                  onMouseEnter={(e) => { const ev = e.evt; setHoverT({ id: t.id, cx: ev.clientX, cy: ev.clientY }); const c = e.target.getStage().container(); if (c) c.style.cursor = 'pointer'; }}
                  onMouseMove={(e) => { const ev = e.evt; setHoverT({ id: t.id, cx: ev.clientX, cy: ev.clientY }); }}
                  onMouseLeave={(e) => { setHoverT((h) => (h && h.id === t.id ? null : h)); const c = e.target.getStage().container(); if (c) c.style.cursor = 'default'; }}>
                  {isRect
                    ? <Rect x={-W / 2} y={-H / 2} width={W} height={H} cornerRadius={Math.min(14, Math.min(W, H) / 3)} fill="rgba(255,255,255,.72)" stroke={stroke} strokeWidth={1.6} />
                    : isSquare
                      ? <Rect x={-W / 2} y={-W / 2} width={W} height={W} cornerRadius={9} fill="rgba(255,255,255,.72)" stroke={stroke} strokeWidth={1.6} />
                      : <Circle radius={W / 2} fill="rgba(255,255,255,.72)" stroke={stroke} strokeWidth={1.6} />}
                  {armed && occ.length < cap ? <Circle radius={rad + 6} stroke="#C6A75A" dash={[4, 4]} strokeWidth={(selGroup || selectedIds.length) ? 1.8 : 1.2} opacity={(selGroup || selectedIds.length) ? 0.9 : 0.7} /> : null}
                  {Array.from({ length: cap }).map((_, si) => {
                    const ang = (si / cap) * Math.PI * 2; const sr = rad + 16;
                    const sx = Math.cos(ang) * sr, sy = Math.sin(ang) * sr;
                    const gid = smap[si]; const g = gid ? guestsById[gid] : null;
                    if (!g) return <Circle key={si} x={sx} y={sy} radius={5.2} fill="#d8d8dd" stroke="#c9c9d0" strokeWidth={0.6} listening={false} />;
                    const parts = (g.name || '').trim().split(/\s+/);
                    const initials = (((parts[0] || '')[0] || '') + ((parts[1] || '')[0] || '')).toUpperCase();
                    const first = parts[0] || g.name;
                    const isSel = selSet.has(gid);
                    const occConflict = (g.conflicts_with || []).some((c) => occ.includes(c));
                    return (
                      // A named, DRAGGABLE seat: drag it onto ANY chair to move the guest to that exact
                      // seat (empty → sit · occupied → swap). Click toggles select.
                      <Group key={si} x={sx} y={sy} draggable={!locked}
                        onDragStart={(e) => { e.cancelBubble = true; setHoverT(null); }}
                        onDragEnd={(e) => {
                          e.cancelBubble = true;
                          const seat = seatAtClient(e.evt.clientX, e.evt.clientY);
                          e.target.position({ x: sx, y: sy });           // snap back; a real move re-renders
                          const layer = e.target.getLayer(); if (layer) layer.batchDraw();
                          if (seat) {
                            if (isSel && selectedIds.length > 1) { if (onRequestMove) onRequestMove(selectedIds, seat.tableId); }
                            else placeAtSeat(gid, seat);
                          }
                        }}
                        onClick={(e) => { e.cancelBubble = true; if (!locked && onToggleSelect) onToggleSelect(gid); }}
                        onTap={(e) => { e.cancelBubble = true; if (!locked && onToggleSelect) onToggleSelect(gid); }}
                        onMouseEnter={(e) => { if (locked) return; const c = e.target.getStage().container(); if (c) c.style.cursor = 'grab'; }}
                        onMouseLeave={(e) => { const c = e.target.getStage().container(); if (c) c.style.cursor = 'default'; }}>
                        {/* Children print as purple triangles, adults as gold circles
                            (host 2026-07-21) — same marks as the 3D room + blueprint. */}
                        {isChild(g)
                          ? <RegularPolygon sides={3} radius={12} fill={occConflict ? '#b04a3a' : (isSel ? '#5d4483' : '#7B5EA7')} stroke={isSel ? '#453264' : '#ffffff'} strokeWidth={isSel ? 1.8 : 1} />
                          : <Circle radius={10} fill={occConflict ? '#b04a3a' : (isSel ? '#9C7B31' : '#C6A75A')} stroke={isSel ? '#6f5a23' : '#ffffff'} strokeWidth={isSel ? 1.8 : 1} />}
                        <Text text={initials} x={-10} y={isChild(g) ? -2 : -4} width={20} align="center" fontSize={8} fontStyle="bold" fill="#fff" listening={false} />
                        <Text text={first} x={-28} y={isChild(g) ? 13 : 11} width={56} align="center" fontFamily="Helvetica, Arial, sans-serif" fontSize={8.5} fill="#2A2622" listening={false} />
                      </Group>
                    );
                  })}
                  {t.accessible ? <Text text="♿" x={rad - 18} y={-rad + 3} fontSize={14} listening={false} /> : null}
                  <Text text={t.label || 'Table'} x={-rad} y={-14} width={rad * 2} align="center" fontFamily="Playfair Display, Georgia, serif" fontSize={13} fill="#2A2622" listening={false} />
                  <Text text={`${occ.length}/${cap}`} x={-rad} y={2} width={rad * 2} align="center" fontSize={10} fill={conflict ? '#b04a3a' : '#988F80'} listening={false} />
                  {/* per-table composition (host 2026-07-21): guests · adults · children */}
                  {occ.length > 0 ? (() => {
                    const kids = occ.filter((gid) => isChild(guestsById[gid])).length;
                    return <Text text={`${occ.length - kids} adults · ${kids} kids`} x={-rad - 14} y={15} width={rad * 2 + 28} align="center" fontSize={8.5} fill={kids ? '#7B5EA7' : '#988F80'} listening={false} />;
                  })() : null}
                </Group>
              );
            })}
          </Layer>
        </Stage>
        {/* marker legend (host 2026-07-21): children read at a glance on the plan */}
        <div className="as-kidlegend" aria-hidden="true">
          <span><i className="lg-adult" /> adult</span>
          <span><i className="lg-kid" /> child</span>
          {locked ? <span>🔒 locked — view only</span> : null}
        </div>
        {hoverT && (() => {
          const t = tablesById[hoverT.id]; if (!t) return null;
          const ids = assignsByTable[hoverT.id] || [];
          const left = Math.min(hoverT.cx + 14, (typeof window !== 'undefined' ? window.innerWidth : 1200) - 280);
          return (
            <div className="as-seatpop" style={{ left, top: hoverT.cy + 14 }}>
              <div className="sp-h">{t.label || 'Table'} · {ids.length}/{Number(t.seat_count) || 8} seated</div>
              {ids.length === 0 ? <div className="sp-empty">No one seated here yet</div>
                : ids.map((gid) => {
                  const g = guestsById[gid] || {};
                  return (
                    <div className="sp-g" key={gid}>
                      <span className="sp-nm">{gc[gid] ? <span className="sp-dot" style={{ background: gc[gid] }} /> : null}{g.name || gid}</span>
                      <span className="sp-tags">
                        {g.household ? <i className="sp-grp">{g.household}</i> : null}
                        {isChild(g) ? <i className="sp-kid" title="Child">▲ child</i> : null}
                        {g.dietary_requirements ? <i title={g.dietary_requirements}>🍽</i> : null}
                        {g.accessibility_needs ? <i title={g.accessibility_needs}>♿</i> : null}
                      </span>
                    </div>
                  );
                })}
            </div>
          );
        })()}
      </div>

      <div className="as-rail">
        <div className="as-railhrow">
          <span className="as-railh" style={{ border: 'none', padding: 0 }}>Unseated · {unseated.length}</span>
          {(groups.real.length > 0) && (
            <span className="as-railseg">
              <button className={`rs ${!byGroup ? 'on' : ''}`} onClick={() => { setByGroup(false); setSelGroup(null); }}>List</button>
              <button className={`rs ${byGroup ? 'on' : ''}`} onClick={() => { setByGroup(true); setSel(null); }} title="Seat households & groups together">By group</button>
            </span>
          )}
        </div>

        {/* multi-select action bar (②) */}
        {selectedIds.length > 0 && (
          <div className="as-selbar" role="status">
            <b>{selectedIds.length} selected</b>
            <span className="hint">Drag any one onto a table, or click a table, to move them together.</span>
            <button className="x" onClick={() => onClearSelection && onClearSelection()}>Clear</button>
          </div>
        )}

        {selGroup && (
          <div className="as-grpcue" role="status">
            Seating <b>{selGroup.name}</b> ({selGroup.ids.length}) — click a table to seat them together.
            <button className="x" title="Cancel" onClick={() => setSelGroup(null)}>✕</button>
          </div>
        )}
        <div className="as-raillist">
          {unseated.length === 0 ? <div className="as-railempty">Everyone's seated ✦</div>
            : byGroup ? (
              <>
                {groups.real.map((grp) => (
                  <div key={grp.key} className={`as-grpclust ${selGroup && selGroup.key === grp.key ? 'sel' : ''}`}>
                    <div className="as-grpclusth">
                      <span className="nm">{grp.name} · {grp.members.length}</span>
                      <button className="as-grpseat" title="Seat this group together"
                        onClick={() => seatGroupAuto(grp)}>Seat together</button>
                    </div>
                    {grp.members.map((g) => (
                      <div key={g.id} className={`as-gchip sub ${selSet.has(g.id) ? 'msel' : ''} ${sel === g.id ? 'sel' : ''}`} draggable
                        onDragStart={(e) => beginDrag(e, g.id)} onDragEnd={endDrag}
                        onClick={() => setSel((s) => (s === g.id ? null : g.id))}>
                        <SelDot id={g.id} />
                        <span className="nm">{gc[g.id] ? <span className="gdot" style={{ background: gc[g.id] }} /> : null}{g.name}</span>
                        <span className="gt">
                          {g.age_group && /child|kid|infant/i.test(g.age_group) ? <i className="kid">child</i> : null}
                          {g.conflicts_with && g.conflicts_with.length ? <i className="warn" title="Has a conflict">⚠</i> : null}
                          {g.accessibility_needs ? <i title="Accessibility need">♿</i> : null}
                        </span>
                      </div>
                    ))}
                  </div>
                ))}
                {groups.singles.length > 0 && <div className="as-railh seated" style={{ marginTop: 6 }}>Individuals · {groups.singles.length}</div>}
                {groups.singles.map((g) => (
                  <div key={g.id} className={`as-gchip ${selSet.has(g.id) ? 'msel' : ''} ${sel === g.id ? 'sel' : ''}`} draggable
                    onDragStart={(e) => beginDrag(e, g.id)} onDragEnd={endDrag}
                    onClick={() => setSel((s) => (s === g.id ? null : g.id))}>
                    <SelDot id={g.id} />
                    <span className="nm">{gc[g.id] ? <span className="gdot" style={{ background: gc[g.id] }} /> : null}{g.name}</span>
                    <span className="gt">
                      {g.age_group && /child|kid|infant/i.test(g.age_group) ? <i className="kid">child</i> : null}
                      {g.conflicts_with && g.conflicts_with.length ? <i className="warn" title="Has a conflict">⚠</i> : null}
                      {g.accessibility_needs ? <i title="Accessibility need">♿</i> : null}
                    </span>
                  </div>
                ))}
              </>
            )
            : unseated.map((g) => (
              <div key={g.id} className={`as-gchip ${selSet.has(g.id) ? 'msel' : ''} ${sel === g.id ? 'sel' : ''}`} draggable
                onDragStart={(e) => beginDrag(e, g.id)} onDragEnd={endDrag}
                onClick={() => setSel((s) => (s === g.id ? null : g.id))}>
                <SelDot id={g.id} />
                <span className="nm">{gc[g.id] ? <span className="gdot" style={{ background: gc[g.id] }} /> : null}{g.name}</span>
                <span className="gt">
                  {g.age_group && /child|kid|infant/i.test(g.age_group) ? <i className="kid">child</i> : null}
                  {g.conflicts_with && g.conflicts_with.length ? <i className="warn" title="Has a conflict">⚠</i> : null}
                  {g.accessibility_needs ? <i title="Accessibility need">♿</i> : null}
                </span>
              </div>
            ))}

          {/* Seated roster (④) — selectable, draggable, inline table edit, print/email */}
          {seated.length > 0 && (
            <div className="as-railhrow seated">
              <span className="as-railh seated" style={{ border: 'none', padding: 0 }}>Seated · {seated.length}</span>
              <span className="as-railexport">
                <button className="rx" title="Print the seated roster" onClick={() => onPrintRoster && onPrintRoster()}>🖨 Print</button>
                <button className="rx" title="Email the seated roster through Communications" onClick={() => onEmailRoster && onEmailRoster()}>✉ Email</button>
              </span>
            </div>
          )}
          {seated.map((g) => (
            <div key={g.id} className={`as-gchip done seatrow ${selSet.has(g.id) ? 'msel' : ''} ${protById[g.id] ? 'prot' : ''}`} draggable
              onDragStart={(e) => beginDrag(e, g.id)} onDragEnd={endDrag}>
              <div className="as-srtop">
                <SelDot id={g.id} />
                <span className="nm">{gc[g.id] ? <span className="gdot" style={{ background: gc[g.id] }} /> : null}{g.name}</span>
              </div>
              <div className="as-srbot">
                <span className="as-srtbl">Table:</span>
                <select className="as-rowtbl" value={assignByGuest[g.id] || ''} title="Move to another table"
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => { const tid = e.target.value; if (tid && tid !== assignByGuest[g.id] && onRequestMove) onRequestMove([g.id], tid); }}>
                  {(tables || []).map((t) => { const short = (t.label || 'Table').replace(/^table\s+/i, ''); return <option key={t.id} value={t.id}>{short || t.label}</option>; })}
                </select>
                <button className={`upin ${protById[g.id] ? 'on' : ''}`}
                  title={protById[g.id] ? 'Protected — optimize won’t move them' : 'Protect (lock in place)'}
                  onClick={() => onProtect && onProtect(g.id, assignByGuest[g.id], !protById[g.id])}>⬩</button>
                <button className="ux" title="Unseat" onClick={() => onUnseat(g.id)}>✕</button>
              </div>
            </div>
          ))}
        </div>
        <div className="as-railhint">{selectedIds.length ? `Move ${selectedIds.length} selected — drag one onto a table or click a table.` : selGroup ? `Click a table to seat ${selGroup.name} together.` : sel ? 'Now click a table to seat them — or drag.' : groups.real.length > 0 ? 'Drag a guest onto a table, ✓ several to move a family, or use “By group”.' : 'Drag a guest onto a table, or ✓ several and move them together. Drag tables to arrange; scroll to zoom.'}</div>
      </div>
    </div>
  );
}
